// Module: ScreenshotBrick
// Role: Self-contained screenshot/image upload brick for flow engine.
//       Single image: presign -> XHR -> submit.
// Uses: hooks/useFileUpload, api/flow-client, shared/limits
// Used by: brick-registry.ts (kind='screenshot')
//
// Pattern: UgcPhotoGridScreen single-photo path.
// ADR-023: no platform literals.

import { useContext, useRef, useState, useCallback } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_UGC_PHOTO_SIZE_BYTES } from '../../../shared/limits';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';

export interface ScreenshotBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

export function ScreenshotBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: ScreenshotBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const handlePickFile = useCallback(() => {
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    if (isDone) return;
    fileInputRef.current?.click();
  }, [canUpload, isDone]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;

      // 5 MB size guard
      if (file.size > MAX_UGC_PHOTO_SIZE_BYTES) {
        setHintMessage('Файл больше 5 МБ. Уменьши и попробуй снова.');
        return;
      }

      setHintMessage(null);

      try {
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'screenshot',
          content_md5: '',
          file_size_bytes: file.size,
        });

        await upload.start({ url: presignResult.upload_url, file });

        // Create thumb for preview
        const objectUrl = URL.createObjectURL(file);
        setThumbUrl(objectUrl);

        // Submit checkpoint with real artefact_path (EC-3)
        await submitCheckpoint.mutateAsync({
          artefact_path: presignResult.artefact_path,
        });

        setIsDone(true);
      } catch {
        setHintMessage('Ошибка загрузки. Попробуй ещё раз.');
      }
    },
    [checkpoint.slug, presign, upload, submitCheckpoint],
  );

  return (
    <Frame>
      <MiniHeader title={checkpoint.slug} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Загрузи скриншот публикации
          </div>
          <div
            style={{
              font: T.footnote,
              color: S.secondaryLabel,
              marginTop: 4,
            }}
          >
            {checkpoint.instructions_text ??
              'Скриншот со статусом «На модерации»'}
          </div>
        </div>

        <InstructionCard p={12}>
          <div
            style={{
              borderRadius: 16,
              border: `2px dashed ${isDone ? P.success : P.terracotta}`,
              background: isDone ? SUCCESS_SOFT_BG : P.terraSoft,
              padding: isDone && thumbUrl ? 0 : '28px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              cursor: canUpload && !isDone ? 'pointer' : 'default',
              overflow: 'hidden',
              minHeight: isDone && thumbUrl ? 200 : undefined,
            }}
            onClick={canUpload && !isDone ? handlePickFile : undefined}
            role={canUpload && !isDone ? 'button' : undefined}
            aria-label={
              isDone ? 'Скриншот загружен' : 'Загрузить скриншот'
            }
            data-testid="screenshot-upload-zone"
          >
            {isDone && thumbUrl ? (
              <img
                src={thumbUrl}
                alt="Загруженный скриншот"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <>
                <Ic
                  name={isDone ? 'check' : 'camera'}
                  size={28}
                  color={isDone ? P.success : P.terracotta}
                />
                <div
                  style={{
                    font: T.subhead,
                    fontWeight: 600,
                    color: isDone ? P.success : S.label,
                  }}
                >
                  {isDone
                    ? 'Скриншот загружен'
                    : 'Нажми, чтобы выбрать скриншот'}
                </div>
              </>
            )}
          </div>

          {upload.status === 'uploading' && upload.progress !== null && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: P.line,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((upload.progress ?? 0) * 100)}%`,
                    background: P.terracotta,
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>
            </div>
          )}
        </InstructionCard>

        {!canUpload && (
          <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>
        )}

        {hintMessage && canUpload && <HintBar>{hintMessage}</HintBar>}
      </div>

      {canUpload && !isDone && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="screenshot-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={isDone ? 'Готово' : 'Загрузить скриншот'}
        onClick={handlePickFile}
        disabled={isDone || upload.status === 'uploading' || !canUpload}
      />
    </Frame>
  );
}
